<template>
  <div
    class="d-flex flex-column gr-6"
    :class="{ 'px-4 mt-4': isMobile }"
  >
    <div
      :class="isMobile ? 'text-h6' : 'text-h5'"
      class="text--text"
    >
      {{ t('Клиники и врачи, которые заказывали отзывы', options) }}
    </div>

    <div class="d-flex flex-column gr-3">
      <div class="text-body-1 text--text">
        {{ t('Заблокировали им любые возможности для продвижения на портале и обнулили их рейтинг.', options) }}
        {{ t('На их странице пациенты увидят предупреждающий баннер.', options) }}
      </div>

      <a
        :href="reverseFull({ name: 'rate_fraud_free' })"
        class="text-body-1 primary--text link-decoration-none"
      >
        {{ t('Условия программы «Отзывы без обмана»', options) }}
      </a>
    </div>

    <v-chip-group
      v-model="tab"
      mandatory
      color="primary"
      active-class="border-primary"
    >
      <v-chip
        value="lpu"
        outlined
      >
        {{ t('Клиники', options) }} {{ lpus.length }}
      </v-chip>
      <v-chip
        value="doctor"
        outlined
      >
        {{ t('Врачи', options) }} {{ doctors.length }}
      </v-chip>
    </v-chip-group>

    <keep-alive>
      <LpuCards
        v-if="tab === 'lpu'"
        class="shame-mark__grid"
        :lpus="lpus"
      />
      <DoctorCards
        v-else
        class="shame-mark__grid"
        :doctors="doctors"
      />
    </keep-alive>
  </div>
</template>

<script lang="ts" setup>
import I18NextVue from 'i18next'
import { computed, ref } from 'vue'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import type { ContextStore } from 'src/components/pages/info/shame-mark/types'
import type { Doctor, Lpu } from 'src/components/pages/info/shame-mark/ShameMarkContent/types'
import DoctorCards from 'src/components/pages/info/shame-mark/ShameMarkContent/components/DoctorCards.vue'
import LpuCards from 'src/components/pages/info/shame-mark/ShameMarkContent/components/LpuCards.vue'
import { getLpuLogoImageFiles } from 'src/utils/getLpuAvatarUrl'

const { t, language } = I18NextVue
const options = { ns: language }

const { context }: ContextStore = useContextStore()
const { reverseFull } = useURLStore()

const isMobile = context.flavour === 'mobile'

function getDateText(dateString: string) {
  try {
    return format(new Date(dateString), 'd MMMM yyyy', { locale: ru })
  } catch {
    return ''
  }
}

const doctors = computed<Doctor[]>(() => (context.doctors ?? []).map(doctor => ({
  avatar: doctor.square_avatar
    ? `/media/${doctor.square_avatar}`
    : '/static/_v1/pd/icons/ui-kit/photo/no-avatar.svg',
  fio: `${doctor.surname} ${doctor.firstname} ${doctor.secondname}`,
  townName: doctor.town.name,
  url: reverseFull({ name: 'doctor', args: [doctor.town.translit, doctor.translit] }),
  fraudMarkExpireDate: getDateText(doctor.fraud_mark_expire_date),
  specialities: doctor.specialities,
  translit: doctor.translit,
})))
const lpus = computed<Lpu[]>(() => (context.lpus ?? []).map(lpu => {
  let avatar = ''

  if (lpu.cropped_logo) {
    avatar = `/media/${lpu.cropped_logo}`
  } else if (lpu.avatar) {
    avatar = `/media/${lpu.avatar}`
  } else {
    avatar = '/static/_v1/pd/icons/ui-kit/photo/no-avatar-lpu.svg'
  }

  return {
    name: lpu.name,
    avatar,
    logoFiles: getLpuLogoImageFiles(lpu),
    townName: lpu.town.name,
    url: reverseFull({ name: 'lpu', args: [lpu.town.translit, lpu.translit] }),
    fraudMarkExpireDate: getDateText(lpu.fraud_mark_expire_date),
    translit: lpu.translit,
  }
}))

const tab = ref('lpu')
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/mixins';

.shame-mark__grid {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(3, 1fr);

  @include media-breakpoint-down(md) {
    grid-template-columns: repeat(2, 1fr);
  }

  @include media-breakpoint-down(sm) {
    grid-template-columns: 1fr;
  }
}
</style>
